/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { CategoricalChartState } from 'recharts/types/chart/types'
import { Box, Paper, Typography } from '@mui/material'
import { InvalidChip, ValidChip } from '../commons/Chips'

export default function DataLineChart({
    data,
    xAxisDataKey = 'timestamp',
    yAxisDataKeys,
    syncId,
    title,
    magnitudes,
    onTimestamp,
}: {
    data: { timestamp: string | number }[]
    xAxisDataKey?: string
    yAxisDataKeys: {
        dataKey: string
        lineColor: string
    }[]
    title?: string
    syncId?: string
    magnitudes?: Record<string, string>
    onTimestamp?: (timestamp: number) => void
}) {
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

    const handleMouseMove = (e: CategoricalChartState) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current)
        }

        debounceTimeout.current = setTimeout(() => {
            try {
                const timestamp = e?.activePayload?.[0]?.payload?.timestamp
                if (!timestamp) return

                onTimestamp && onTimestamp(timestamp)
            } catch (e) {
                console.log(e)
            }
            debounceTimeout.current = null // Clear the ref once the timeout is done
        }, 200)
    }
    return (
        <Box>
            {title && (
                <Typography variant='h6' textAlign='center' color='black'>
                    {title}
                </Typography>
            )}
            <LineChart
                width={(window.innerWidth * 0.98) / 2}
                height={window.innerHeight * 0.6}
                data={data}
                syncId={syncId}
                margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                }}
                onMouseMove={onTimestamp && handleMouseMove}
            >
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis
                    allowDataOverflow
                    dataKey={xAxisDataKey}
                    tickFormatter={(value: string) => new Date(value).toLocaleTimeString()}
                    minTickGap={20}
                    interval='preserveStartEnd'
                />
                <YAxis allowDataOverflow yAxisId='1' />
                {yAxisDataKeys.map(({ dataKey, lineColor }) => (
                    <Line
                        key={dataKey}
                        type='monotone'
                        dot={false}
                        yAxisId='1'
                        dataKey={dataKey}
                        stroke={lineColor}
                        fill={lineColor}
                    />
                ))}
                <Tooltip content={<CustomTooltip magnitudes={magnitudes} />} />
            </LineChart>
        </Box>
    )
}

const CustomTooltip = ({ active, payload, label, magnitudes }: any) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{ backgroundColor: 'white', p: 1, opacity: 0.5 }}>
                <Typography color='black' align='center'>
                    <strong>{new Date(label).toLocaleTimeString()}</strong>
                </Typography>
                {payload.map((p: any, i: number) => {
                    return (
                        <Typography key={i} align='center' color={p.fill}>
                            {p.dataKey}:{' '}
                            <strong>
                                {p.value} {magnitudes && magnitudes[p.dataKey]}
                            </strong>
                        </Typography>
                    )
                })}

                {payload[0]?.payload?.message && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Typography color='grey'>Message</Typography>
                        <Typography color='black'>{payload[0].payload.message}</Typography>
                    </Box>
                )}
                {'checksum' in payload[0].payload && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography color='grey'>checksum</Typography>
                        <Typography color='black'>{payload[0].payload.checksum}</Typography>
                        {payload[0].payload.valid ? <ValidChip size='small' /> : <InvalidChip size='small' />}
                    </Box>
                )}
            </Paper>
        )
    }

    return null
}
